import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgForage } from 'ngforage';
import { Observable } from 'rxjs';
import AssetHelper from 'src/app/helpers/asset-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Asset from 'src/app/model/asset';
import { SubProject } from 'src/app/model/project/sub-project';
import { ImmoIPNotExecutedHistoryItemObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-not-executed-history-item-object';
import { ModalService } from 'src/app/services/modal.service';
import { OfflineAsset } from 'src/app/services/placer-offline-changes/offline-asset';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ImmoIPPlacerTourItemDetailChangeStorageHandler } from '../../helper/immo-ip-placer-tour-item-detail-change-storage-handler';
import { ImmoIPPlacerTourItemDetailHelper } from '../../helper/immo-ip-placer-tour-item-detail-helper';
import { FileHelper } from '../../../../../../helpers/file-helper';

@Component({
  selector: 'immo-ip-placer-tour-item-detail-not-executed-assets',
  templateUrl:
    './immo-ip-placer-tour-item-detail-not-executed-assets.component.html',
  styleUrls: [
    './immo-ip-placer-tour-item-detail-not-executed-assets.component.scss',
  ],
})
export class ImmoIPPlacerTourItemDetailNotExecutedAssetsComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy
{
  @Input()
  notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject;

  @Input()
  subProject: SubProject;

  @Input()
  immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler;

  @Input()
  shouldReloadNotExecutedHistoryItemAssets: Observable<void>;

  @Input()
  disabled: boolean = false;

  @Input()
  flexImages: boolean = true;

  @Output()
  addedAssetUuids = new EventEmitter<string[]>();

  @Output()
  assetDeleted = new EventEmitter<Asset>();

  blobUrls: string[];
  toShowAssets: Asset[];
  private _addedAssetUuids: string[];
  isImage = FileHelper.isImage;
  init: boolean = false;

  constructor(
    private ngForage: NgForage,
    private sanitizer: DomSanitizer,
    private modalService: ModalService
  ) {
    super();
  }

  ngOnInit(): void {
    if (
      CollectionHelper.isPopulated(
        this.notExecutedHistoryItemObject.offlineAssets
      )
    ) {
      this.instantiateAssetsFromOfflineAssets(
        this.notExecutedHistoryItemObject.offlineAssets
      );
    }

    if (
      CollectionHelper.isPopulated(this.notExecutedHistoryItemObject.assets)
    ) {
      this.toShowAssets = !CollectionHelper.isPopulated(this.toShowAssets)
        ? this.notExecutedHistoryItemObject.assets.map((a) => {
            return { ...a.asset, productAssetId: a.id };
          })
        : [
            ...this.toShowAssets,
            ...this.notExecutedHistoryItemObject.assets.map((a) => {
              return { ...a.asset, productAssetId: a.id };
            }),
          ];
    }

    if (this.shouldReloadNotExecutedHistoryItemAssets != null) {
      this.addSubscription(this.shouldReloadNotExecutedHistoryItemAssets, () =>
        this.handleReloadOfflineAssets()
      );
    }

    this.init = true;
  }

  private handleReloadOfflineAssets(): void {
    if (
      !CollectionHelper.isPopulated(
        this.notExecutedHistoryItemObject.offlineAssets
      )
    ) {
      this.toShowAssets = null;
    } else {
      this.instantiateAssetsFromOfflineAssets(
        this.notExecutedHistoryItemObject.offlineAssets
      );
    }
  }

  private createLocalSafeUrl(asset: Asset): void {
    if (this.blobUrls == null) {
      this.blobUrls = [];
    }
    const url = URL.createObjectURL(asset.localFile);
    asset.localSafeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
    this.blobUrls.push(url);
  }

  ngOnDestroy(): void {
    if (CollectionHelper.isPopulated(this.blobUrls)) {
      this.blobUrls.forEach((u) => URL.revokeObjectURL(u));
    }
  }

  onAssetsAdded(assets: Asset[]): void {
    if (this._addedAssetUuids == null) {
      this._addedAssetUuids = [];
    }
    if (this.toShowAssets == null) {
      this.toShowAssets = [];
    }
    if (this.notExecutedHistoryItemObject.offlineAssets == null) {
      this.notExecutedHistoryItemObject.offlineAssets = [];
    }
    assets.forEach((a) => {
      this.createLocalSafeUrl(a);
      this.notExecutedHistoryItemObject.offlineAssets.push(
        this.copyAssetToOfflineAsset(a)
      );
      this._addedAssetUuids.push(a.localFileUuid);
      this.toShowAssets.push(a);
    });
    ImmoIPPlacerTourItemDetailHelper.handleNotExecutedAssetAdded(
      this.subProject,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
    );
    this.addedAssetUuids.emit(this._addedAssetUuids);
  }

  private async instantiateAssetsFromOfflineAssets(
    offlineAssets: OfflineAsset[]
  ): Promise<void> {
    const toShowAssets: Asset[] = [];
    for (const offlineAsset of offlineAssets) {
      const asset = await this.instantiateAsset(offlineAsset);
      this.createLocalSafeUrl(asset);
      toShowAssets.push(asset);
    }
    this.toShowAssets = toShowAssets;
  }

  private async instantiateAsset(offlineAsset: OfflineAsset): Promise<Asset> {
    const file: File = await this.ngForage.getItem<File>(
      AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
    );
    return {
      uuid: offlineAsset.uuid,
      name: offlineAsset.name,
      type: offlineAsset.type,
      size: file.size,
      base64: null,
      localFile: file,
      localFileUuid: offlineAsset.localFileUuid,
    };
  }

  private copyAssetToOfflineAsset(asset: Asset): OfflineAsset {
    return {
      uuid: asset.uuid,
      name: asset.name,
      type: asset.type,
      formSection: null,
      localFileUuid: asset.localFileUuid,
    };
  }

  removeAsset(asset: Asset, index: number): void {
    this.modalService.confirmDialog(
      'Foto verwijderen',
      'Bent u zeker dat u deze foto wilt verwijderen?',
      (b: boolean) => {
        if (b) {
          this.doRemoveAsset(asset, index);
        }
      }
    );
  }

  private doRemoveAsset(asset: Asset, index: number): void {
    if (asset['productAssetId'] != null) {
      const assetIndex = this.notExecutedHistoryItemObject.assets.findIndex(
        (a) => a.id === asset['productAssetId']
      );
      this.notExecutedHistoryItemObject.assets.splice(assetIndex, 1);
    } else {
      const assetIndex =
        this.notExecutedHistoryItemObject.offlineAssets.findIndex(
          (a) => a.localFileUuid === asset.localFileUuid
        );
      this.notExecutedHistoryItemObject.offlineAssets.splice(assetIndex, 1);
      this.ngForage.removeItem(
        AssetHelper.getStorageKeyFor(asset.localFileUuid)
      );
      CollectionHelper.filterInPlace(
        this._addedAssetUuids,
        (uuid) => uuid !== asset.localFileUuid
      );
      this.addedAssetUuids.emit(this._addedAssetUuids);
    }
    this.toShowAssets.splice(index, 1);
    this.assetDeleted.emit(asset);
    this.immoIPPlacerTourItemDetailChangeStorageHandler.deleteNotExecutedHistoryItemAsset(
      asset.uuid
    );
  }
}
